import * as React from 'react';
import { useState } from 'react';
import { Trash2, ChevronsUpDown, Check } from 'lucide-react';
import {
  SEGMENT_FIELD_REGISTRY,
  segmentFieldEnum,
  type SegmentCondition,
  type SegmentField,
  type SegmentOperator,
} from '@masters-club/validation';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { SegmentValueInput } from './SegmentValueInput';

const OPERATOR_LABELS: Record<SegmentOperator, string> = {
  eq: 'равно',
  neq: 'не равно',
  contains: 'содержит',
  in: 'входит в список',
  notIn: 'не входит в список',
  gt: 'больше',
  gte: 'больше или равно',
  lt: 'меньше',
  lte: 'меньше или равно',
  between: 'в диапазоне',
  isSet: 'заполнено',
  isNotSet: 'не заполнено',
  withinLastDays: 'за последние N дней',
  hasActive: 'есть активный доступ',
  expired: 'доступ истёк',
  revoked: 'доступ отозван',
  purchased: 'куплен',
  notPurchased: 'не куплен',
  completed: 'пройден',
  notCompleted: 'не пройден',
  started: 'начат',
};

// Все поля из реестра
const ALL_FIELDS = segmentFieldEnum.options;

type GroupName =
  | 'Профиль'
  | 'Статус'
  | 'Атрибуция'
  | 'Абонементы'
  | 'Платежи'
  | 'Теги'
  | 'Активность'
  | 'Доступ'
  | 'Покупки'
  | 'Прогресс';

const GROUPS: GroupName[] = [
  'Профиль',
  'Статус',
  'Атрибуция',
  'Абонементы',
  'Платежи',
  'Теги',
  'Активность',
  'Доступ',
  'Покупки',
  'Прогресс',
];

const FIELDS_BY_GROUP: Record<GroupName, SegmentField[]> = (() => {
  const result = {} as Record<GroupName, SegmentField[]>;
  for (const g of GROUPS) {
    result[g] = [];
  }
  for (const field of ALL_FIELDS) {
    const meta = SEGMENT_FIELD_REGISTRY[field];
    const group = meta.group as GroupName;
    if (group in result) {
      result[group].push(field);
    }
  }
  return result;
})();

interface SegmentConditionEditorProps {
  condition: SegmentCondition;
  onChange: (next: SegmentCondition) => void;
  onRemove: () => void;
  disabled?: boolean;
}

export function SegmentConditionEditor({
  condition,
  onChange,
  onRemove,
  disabled = false,
}: SegmentConditionEditorProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const meta = SEGMENT_FIELD_REGISTRY[condition.field];

  const handleFieldChange = (field: SegmentField) => {
    const newMeta = SEGMENT_FIELD_REGISTRY[field];
    const firstOp = newMeta.operators[0];
    onChange({ field, operator: firstOp, value: undefined });
    setOpen(false);
    setSearch('');
  };

  const handleOperatorChange = (operator: SegmentOperator) => {
    onChange({ ...condition, operator, value: undefined });
  };

  const searchLower = search.toLowerCase();
  const filteredGroups = GROUPS.map((group) => {
    const fields = FIELDS_BY_GROUP[group].filter((field) => {
      if (!search) { return true; }
      return SEGMENT_FIELD_REGISTRY[field].label.toLowerCase().includes(searchLower);
    });
    return { group, fields };
  }).filter(({ fields }) => fields.length > 0);

  return (
    <div className="flex flex-col gap-2 p-3 rounded-md border bg-background">
      <div className="flex items-start gap-2">
        {/* Combobox выбора поля с поиском и группировкой */}
        <div className="flex-1 min-w-0">
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                aria-expanded={open}
                className="w-full justify-between font-normal"
                disabled={disabled}
              >
                <span className="truncate">
                  {meta ? meta.label : 'Выберите поле'}
                </span>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[260px] p-0" align="start">
              <Command shouldFilter={false}>
                <CommandInput
                  placeholder="Поиск поля..."
                  value={search}
                  onValueChange={setSearch}
                />
                <CommandList>
                  <CommandEmpty>Поля не найдены</CommandEmpty>
                  {filteredGroups.map(({ group, fields }) => (
                    <CommandGroup key={group} heading={group}>
                      {fields.map((field) => (
                        <CommandItem
                          key={field}
                          value={field}
                          onSelect={() => handleFieldChange(field)}
                        >
                          <Check
                            className={cn(
                              'mr-2 h-4 w-4 shrink-0',
                              condition.field === field ? 'opacity-100' : 'opacity-0',
                            )}
                          />
                          {SEGMENT_FIELD_REGISTRY[field].label}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  ))}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        {/* Select оператора — только допустимые для поля */}
        <div className="flex-1 min-w-0">
          <Select
            value={condition.operator}
            onValueChange={(v) => handleOperatorChange(v as SegmentOperator)}
            disabled={disabled}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Оператор" />
            </SelectTrigger>
            <SelectContent>
              {meta.operators.map((op) => (
                <SelectItem key={op} value={op}>
                  {OPERATOR_LABELS[op]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Кнопка удаления */}
        <Button
          variant="ghost"
          size="sm"
          onClick={onRemove}
          disabled={disabled}
          className="text-muted-foreground hover:text-destructive shrink-0"
          aria-label="Удалить условие"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>

      {/* Value input — SegmentValueInput сам решает что рендерить */}
      <SegmentValueInput
        meta={meta}
        operator={condition.operator}
        value={condition.value}
        onChange={(value) => onChange({ ...condition, value })}
        disabled={disabled}
      />
    </div>
  );
}
